/**
 * Admin · Proposals list + inline builder (Phase 3)
 *
 * Single-screen experience: a list view on the left with status filter
 * chips + search, and an inline detail drawer on the right that doubles
 * as the builder (line items, customer info, send/resend/cancel/PDF
 * actions).
 *
 * Approved proposals are shown read-only with the locked snapshot — see
 * the audit log + the customer's selected/declined breakdown.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Search, Send, Copy, Eye, FileDown, X, Trash2, RefreshCw,
  Check, Lock, AlertCircle, ImageIcon, ExternalLink,
} from "lucide-react";
import { PortalShell } from "@/components/PortalShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ShowTestDataToggle, useShowDemoToggle } from "@/components/ShowTestDataToggle";
import { ArchiveStatusFilter, useArchiveFilter } from "@/components/ArchiveStatusFilter";
import { ArchiveActionsMenu } from "@/components/ArchiveActionsMenu";
import { StatusBadge as ArchiveStatusBadge } from "@/components/StatusBadge";
import api, { formatApiError } from "@/lib/api";
import { formatDateTimeAU } from "@/lib/formatDateAU";
import { resolveImageUrl } from "@/lib/imageUrl";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const money = (cents) => {
  const amount = (cents || 0) / 100;
  return new Intl.NumberFormat("en-AU", {
    style: "currency", currency: "AUD", currencyDisplay: "narrowSymbol",
  }).format(amount);
};

const STATUS_TABS = [
  { value: "",          label: "All" },
  // Phase 2L-A104 — customer-initiated requests (PDF link click) land
  // here with status="requested". Surface as its own tab so admins
  // never have to scan the All list to find them.
  { value: "requested", label: "Customer requests" },
  { value: "draft",     label: "Draft" },
  { value: "sent",      label: "Sent" },
  { value: "viewed",    label: "Viewed" },
  { value: "approved",  label: "Approved" },
  { value: "declined",  label: "Declined" },
  { value: "expired",   label: "Expired" },
  { value: "cancelled", label: "Cancelled" },
];

// Phase 2L-A125 follow-up — synthetic filter value used by the
// "Needs attention" chip. The backend exposes `needs_attention=true`
// as a separate query parameter (independent of `status`), so we use
// this sentinel in the local UI state to drive the right request.
const NEEDS_ATTENTION_FILTER = "__needs_attention__";

// Map of attention reasons → short user-facing chip text.
const ATTENTION_REASON_LABEL = {
  legacy_normalized: "Legacy row normalised",
  missing_product:   "Missing product",
  stale_request:     "Requested > 7 days",
};

const STATUS_BADGE = {
  requested: "bg-cyan-100 text-cyan-800",
  draft:     "bg-slate-100 text-slate-700",
  sent:      "bg-amber-100 text-amber-800",
  viewed:    "bg-blue-100 text-blue-800",
  approved:  "bg-emerald-100 text-emerald-800",
  declined:  "bg-rose-100 text-rose-800",
  expired:   "bg-slate-200 text-slate-600",
  cancelled: "bg-slate-200 text-slate-600",
};

// Phase 2J-AI — customer-visible label for each status. The underlying
// DB enum stays exactly as-is (`approved`, `sent`, etc.) so audit logs,
// queries, and downstream services are unaffected. We only swap the
// rendered label so the customer never sees the word "Approved".
const STATUS_LABEL = {
  requested: "Customer Requested",
  draft:     "Draft",
  sent:      "Estimate Sent",
  viewed:    "Viewed",
  approved:  "Estimate Accepted",
  declined:  "Estimate Declined",
  expired:   "Estimate Expired",
  cancelled: "Estimate Cancelled",
};

const LINE_STATUSES = [
  { value: "required",    label: "Required" },
  { value: "recommended", label: "Recommended" },
  { value: "optional",    label: "Optional" },
];

function StatusBadge({ status }) {
  return (
    <span data-testid={`proposal-status-${status}`} className={`inline-block text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full ${STATUS_BADGE[status] || "bg-slate-100 text-slate-600"}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

// ============================================================ DETAIL DRAWER

// Phase 2L-A27 — exported for reuse by /admin/proposal-presets (Step 2).
// These two are deliberate named exports so the preset management page
// can reuse the exact same UI shape admins already learned in the proposal
// builder. Default export remains AdminProposals (the page component).
export function LineEditor({ line, index, products, optionGroups, onChange, onRemove, locked }) {
  const product = products.find((p) => p.id === line.product_id);
  const unit = product?.unit_price_cents || 0;
  const canBeInGroup = (line.line_status || "optional") === "optional";
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3 space-y-2">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden">
          {product?.image_url ? (
            <img
              src={resolveImageUrl(product.image_url)}
              alt=""
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <ImageIcon className="w-5 h-5 text-slate-400" />
          )}
        </div>
        <select
          disabled={locked}
          data-testid={`line-product-${index}`}
          value={line.product_id || ""}
          onChange={(e) => onChange({ ...line, product_id: e.target.value })}
          className="flex-1 min-w-0 h-9 border border-slate-300 rounded-md px-2 text-sm"
        >
          <option value="">— select product —</option>
          {products.map((p) => {
            const isInactive = p.active === false;
            // Phase 2L-A31 guardrail: an inactive product is selectable
            // ONLY if it's already the current selection on this line.
            // That way existing lines (and preset-applied lines that
            // legitimately reference an inactive SKU) keep working,
            // while brand-new lines can't accidentally pick a
            // deprecated product from the dropdown.
            const isCurrent = p.id === line.product_id;
            return (
              <option
                key={p.id}
                value={p.id}
                disabled={isInactive && !isCurrent}
              >
                {p.name} · {money(p.unit_price_cents)}{isInactive ? " · (inactive)" : ""}
              </option>
            );
          })}
        </select>
        <select
          disabled={locked}
          data-testid={`line-status-${index}`}
          value={line.line_status}
          onChange={(e) => {
            // If switching away from optional and we're in a group, clear the group binding.
            const next = { ...line, line_status: e.target.value };
            if (e.target.value !== "optional") next.option_group_id = null;
            onChange(next);
          }}
          className="h-9 border border-slate-300 rounded-md px-2 text-sm"
        >
          {LINE_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <Input
          type="number"
          disabled={locked}
          value={line.qty}
          onChange={(e) => onChange({ ...line, qty: Number(e.target.value) || 0 })}
          className="w-20 h-9 text-sm"
          min={0}
          step="0.5"
        />
        <div className="text-right text-sm tabular-nums text-slate-700 w-24">{money(unit * (Number(line.qty) || 0))}</div>
        {!locked && (
          <button onClick={onRemove} className="text-rose-500 hover:text-rose-700 p-1.5 rounded-lg">
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
      {/* Phase 2L-A25 — assign this line to a Choose-one option group.
          Only optional lines can be grouped (required is always included;
          recommended is a non-grouped add-on). */}
      {canBeInGroup && optionGroups && optionGroups.length > 0 && (
        <div className="flex items-center gap-3 pl-[60px]">
          <Label className="text-[10px] uppercase font-bold tracking-wider text-slate-500 whitespace-nowrap">
            Option group
          </Label>
          <select
            disabled={locked}
            data-testid={`line-group-${index}`}
            value={line.option_group_id || ""}
            onChange={(e) => onChange({ ...line, option_group_id: e.target.value || null })}
            className="flex-1 h-8 border border-slate-300 rounded-md px-2 text-xs"
          >
            <option value="">— standalone (not in any group) —</option>
            {optionGroups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.label || "Untitled group"}
              </option>
            ))}
          </select>
        </div>
      )}
      {!canBeInGroup && line.option_group_id && (
        <div className="pl-[60px] text-[11px] text-amber-700">
          ⚠ Group assignment will be cleared on save (required/recommended lines can&apos;t be in a group).
        </div>
      )}
    </div>
  );
}

/** Phase 2L-A25 — Admin option-group editor.
 *  Each group has: label, description, recommended_badge_text,
 *  default_product_id (optional explicit override).
 *  Membership is managed on each LineEditor row (option_group_id).
 */
export function OptionGroupsEditor({ groups, lines, products, onChange, locked }) {
  const updateGroup = (idx, patch) => {
    onChange(groups.map((g, i) => (i === idx ? { ...g, ...patch } : g)));
  };
  const addGroup = () => {
    const newId = `grp-${Math.random().toString(36).slice(2, 9)}`;
    onChange([
      ...groups,
      {
        id: newId,
        label: "",
        description: "",
        recommended_badge_text: "Recommended",
        default_product_id: null,
      },
    ]);
  };
  const removeGroup = (idx) => {
    onChange(groups.filter((_, i) => i !== idx));
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500">
            Choose-one option groups
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            Group multiple model choices so the customer picks one. Highest-priced
            option is the default unless you set one explicitly.
          </div>
        </div>
        {!locked && (
          <Button
            data-testid="add-option-group-btn"
            variant="outline"
            onClick={addGroup}
            className="rounded-full"
            size="sm"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Add option group
          </Button>
        )}
      </div>

      {groups.length === 0 && (
        <div className="text-center py-4 text-[11px] text-slate-400">
          No option groups. Use these when you want to offer multiple
          competing model choices (e.g. T20 / T20UV / TR20UV) and let
          the customer pick exactly one.
        </div>
      )}

      <div className="space-y-3">
        {groups.map((g, gi) => {
          const members = (lines || []).filter((ln) => ln.option_group_id === g.id);
          const memberCount = members.length;
          // Highest-priced fallback for the helper text.
          const fallback = members
            .map((ln) => products.find((p) => p.id === ln.product_id))
            .filter(Boolean)
            .sort((a, b) => (b.unit_price_cents || 0) - (a.unit_price_cents || 0))[0];
          return (
            <div
              key={g.id}
              data-testid={`option-group-row-${gi}`}
              className="border border-slate-200 rounded-xl p-3 bg-slate-50/40 space-y-2"
            >
              <div className="flex items-center gap-2">
                <Input
                  data-testid={`group-label-${gi}`}
                  placeholder="Group title (e.g. Choose your system option)"
                  value={g.label || ""}
                  onChange={(e) => updateGroup(gi, { label: e.target.value })}
                  disabled={locked}
                  className="flex-1 h-9 text-sm"
                />
                <Input
                  data-testid={`group-badge-${gi}`}
                  placeholder='Badge text (e.g. "Recommended")'
                  value={g.recommended_badge_text || ""}
                  onChange={(e) => updateGroup(gi, { recommended_badge_text: e.target.value })}
                  disabled={locked}
                  className="w-44 h-9 text-sm"
                />
                {!locked && (
                  <button
                    data-testid={`group-remove-${gi}`}
                    onClick={() => removeGroup(gi)}
                    className="text-rose-500 hover:text-rose-700 p-1.5 rounded-lg"
                    title="Remove group"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              <Textarea
                placeholder="Short description shown above the options (optional)"
                value={g.description || ""}
                onChange={(e) => updateGroup(gi, { description: e.target.value })}
                disabled={locked}
                rows={2}
                className="text-xs"
              />
              <div className="flex items-center gap-2">
                <Label className="text-[10px] uppercase font-bold tracking-wider text-slate-500 whitespace-nowrap">
                  Default option
                </Label>
                <select
                  data-testid={`group-default-${gi}`}
                  value={g.default_product_id || ""}
                  onChange={(e) =>
                    updateGroup(gi, { default_product_id: e.target.value || null })
                  }
                  disabled={locked}
                  className="flex-1 h-8 border border-slate-300 rounded-md px-2 text-xs"
                >
                  <option value="">
                    Auto — highest-priced wins
                    {fallback ? ` (${fallback.name})` : ""}
                  </option>
                  {members.map((ln) => {
                    const p = products.find((pp) => pp.id === ln.product_id);
                    return (
                      <option key={ln.product_id} value={ln.product_id}>
                        {p?.name || ln.product_id} · {money(p?.unit_price_cents || 0)}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className="text-[11px] text-slate-500 flex items-center justify-between pt-1">
                <span>
                  <strong>{memberCount}</strong>{" "}
                  {memberCount === 1 ? "option assigned" : "options assigned"}
                  {memberCount === 0 && (
                    <span className="text-amber-700">
                      {" "}
                      — assign at least one line item to this group below.
                    </span>
                  )}
                </span>
                <span className="text-slate-400">
                  If no default is selected, the highest-priced option is the default.
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function ApprovedLineRow({ line }) {
  // Phase 2L-A28 (Estimate Presentation Polish): mirror the public viewer's
  // compact description + "View details" link so admins see the customer view.
  const short = (line.short_description || "").trim();
  const legacy = (line.description || "").trim();
  const desc = short || (legacy.length > 140 ? legacy.slice(0, 137).trimEnd() + "…" : legacy) || null;
  const detailUrl = (line.detail_url || "").trim();
  return (
    <div className={`flex items-start gap-3 p-3 rounded-xl border ${line.selected ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200 opacity-70"}`}>
      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center overflow-hidden">
        {line.image_url ? (
          <img
            src={resolveImageUrl(line.image_url)}
            alt=""
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <ImageIcon className="w-4 h-4 text-slate-400" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-900 truncate">{line.name}</div>
        {desc && (
          <div
            data-testid={`approved-line-${line.product_id}-desc`}
            className="text-xs text-slate-600 mt-0.5 leading-snug line-clamp-2"
            title={desc}
          >
            {desc}
          </div>
        )}
        {detailUrl && (
          <a
            data-testid={`approved-line-${line.product_id}-details`}
            href={detailUrl}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-cyan-700 hover:underline mt-1"
          >
            View details <ExternalLink className="w-3 h-3" />
          </a>
        )}
        <div className="text-[11px] text-slate-500 mt-1">
          <span className="uppercase font-bold tracking-wider">{line.line_status}</span>
          <span className="mx-1.5 text-slate-300">·</span>
          ×{line.qty}
        </div>
      </div>
      <div className="text-right">
        <div className="text-sm font-bold tabular-nums">{money(line.line_total_cents)}</div>
        <div className={`text-[10px] uppercase font-bold tracking-wider ${line.selected ? "text-emerald-700" : "text-rose-600"}`}>
          {line.selected ? "✓ Included" : "✗ Declined"}
        </div>
      </div>
    </div>
  );
}

function ProposalDetail({ id, products, onClose, onChanged }) {
  const [doc, setDoc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null); // editable draft form
  const [reloadKey, setReloadKey] = useState(0); // bump to force re-fetch
  const [presetPickerOpen, setPresetPickerOpen] = useState(false); // Phase 2L-A27 Step 3
  const [presetMergeChoice, setPresetMergeChoice] = useState(null); // Phase 2L-A32 merge-vs-replace

  useEffect(() => {
    let cancelled = false;
    api.get(`/admin/proposals/${id}/hydrated`).then(({ data }) => {
      if (cancelled) return;
      setDoc(data);
      const rawLines = (data.lines || []).map((l) => ({
        product_id: l.product_id,
        qty: l.qty,
        line_status: l.line_status,
        notes: l.notes || null,
        option_group_id: l.option_group_id || null,
      }));
      setForm({
        customer_name: data.customer_name || "",
        customer_email: data.customer_email || "",
        customer_phone: data.customer_phone || "",
        property_address: data.property_address || "",
        test_id: data.test_id || "",
        notes: data.notes || "",
        brand: data.brand || "pristine_water_systems",
        lines: rawLines,
        option_groups: (data.option_groups || []).map((g) => ({
          id: g.id,
          label: g.label || "",
          description: g.description || "",
          recommended_badge_text: g.recommended_badge_text || "Recommended",
          default_product_id: g.default_product_id || null,
        })),
      });
    });
    return () => { cancelled = true; };
  }, [id, reloadKey]);

  const load = () => setReloadKey((k) => k + 1);

  // Phase 2L-A30: dynamic subtotal label parts — only mention sections that
  // actually have at least one contributing line. Avoids the misleading
  // "(required + default options + recommended add-ons)" hardcoded text on
  // an option-group-only preset (PRD complaint: looks like sections "should"
  // be there even when they aren't).
  const subtotalLabelParts = useMemo(() => {
    if (!form) return [];
    const lines = form.lines || [];
    const groups = form.option_groups || [];
    const parts = [];
    if (lines.some((l) => l.product_id && l.line_status === "required")) {
      parts.push("required");
    }
    if (groups.some((g) => lines.some((l) => l.option_group_id === g.id && l.product_id))) {
      parts.push("default options");
    }
    if (lines.some((l) => l.product_id && l.line_status === "recommended" && !l.option_group_id)) {
      parts.push("recommended add-ons");
    }
    return parts;
  }, [form]);


  // Phase 2L-A25 — Admin "starting total" preview using the SAME rules
  // the customer sees on the public proposal:
  //   required items + (default option per group) + (recommended add-ons that are pre-selected)
  // Optional non-group lines and non-default group siblings are NOT counted.
  const subtotal = useMemo(() => {
    if (!form) return 0;
    const lines = form.lines || [];
    const groups = form.option_groups || [];
    // Resolve the "default" product_id per group: explicit override wins,
    // otherwise highest-priced member.
    const groupDefault = {};
    for (const g of groups) {
      const members = lines.filter((l) => l.option_group_id === g.id && l.product_id);
      if (members.length === 0) continue;
      if (g.default_product_id && members.some((m) => m.product_id === g.default_product_id)) {
        groupDefault[g.id] = g.default_product_id;
      } else {
        const ranked = members
          .map((m) => ({ pid: m.product_id, price: (products.find((p) => p.id === m.product_id)?.unit_price_cents) || 0 }))
          .sort((a, b) => b.price - a.price);
        groupDefault[g.id] = ranked[0].pid;
      }
    }
    let total = 0;
    for (const l of lines) {
      if (!l.product_id) continue;
      const unit = products.find((p) => p.id === l.product_id)?.unit_price_cents || 0;
      const lineTotal = unit * (Number(l.qty) || 0);
      const status = l.line_status || "optional";
      if (status === "required") {
        total += lineTotal;
      } else if (l.option_group_id) {
        if (groupDefault[l.option_group_id] === l.product_id) total += lineTotal;
      } else if (status === "recommended") {
        total += lineTotal;
      }
    }
    return total;
  }, [form, products]);

  if (!doc || !form) {
    return (
      <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-slate-900/50 backdrop-blur-sm">
        <div className="bg-white w-full max-w-3xl h-full overflow-y-auto p-6 shadow-2xl">
          <div className="text-sm text-slate-500">Loading…</div>
        </div>
      </div>
    );
  }
  const locked = ["approved", "declined", "expired"].includes(doc.status);
  const isApproved = doc.status === "approved";

  const updateLine = (idx, patch) => {
    setForm((f) => ({ ...f, lines: f.lines.map((l, i) => i === idx ? patch : l) }));
  };
  const addLine = () => {
    setForm((f) => ({ ...f, lines: [...f.lines, { product_id: "", qty: 1, line_status: "optional", option_group_id: null }] }));
  };
  const removeLine = (idx) => {
    setForm((f) => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));
  };
  const updateGroups = (groups) => setForm((f) => ({ ...f, option_groups: groups }));

  // Phase 2L-A27 Step 3 + A32 — Apply a saved preset to this proposal.
  // Behaviour:
  //   - Empty draft (no lines AND no option groups) → apply immediately,
  //     same as before. No modal.
  //   - Non-empty draft → open a confirmation modal so the admin decides
  //     whether to APPEND (merge) or REPLACE the current draft contents.
  //   - Approved proposals → backend rejects (409); we never reach here
  //     because the picker is hidden in approved view anyway.
  const applyPreset = (presetId) => {
    const hasExistingLines = (form?.lines || []).some((l) => l.product_id);
    const hasExistingGroups = (form?.option_groups || []).length > 0;
    if (hasExistingLines || hasExistingGroups) {
      setPresetMergeChoice({ presetId });
      return;
    }
    // Empty draft path → apply immediately (preserves the original UX
    // for fresh estimates).
    _applyPresetWithMode(presetId, false);
  };

  const _applyPresetWithMode = async (presetId, replaceExisting) => {
    try {
      const { data } = await api.post(
        `/admin/proposals/${id}/apply-preset/${presetId}`,
        null,
        { params: { replace_existing: replaceExisting } },
      );
      toast.success(replaceExisting
        ? "Preset applied. Existing draft contents were replaced."
        : "Preset added to estimate.");
      setPresetPickerOpen(false);
      setPresetMergeChoice(null);
      // The merge endpoint returns the hydrated proposal; bump the
      // reloadKey so the existing load effect re-fetches the raw form
      // state with the new lines + groups.
      setReloadKey((k) => k + 1);
      onChanged?.();
    } catch (e) {
      // Backend's clear error messages: "Approved estimates are locked …"
      // (409) and "Preset is inactive or not found." (404).
      const detail = e.response?.data?.detail || e.message;
      const status = e.response?.status;
      if (status === 409) {
        toast.error("Approved estimates cannot be changed.");
      } else if (status === 404) {
        toast.error("Preset is inactive or not found.");
      } else {
        toast.error(detail);
      }
    }
  };

  // Phase 2L-A25 client-side guards. Backend enforces the same rules
  // (400 with clear detail), but a pre-flight check gives nicer toasts.
  const validate = () => {
    const groups = form.option_groups || [];
    const groupIds = new Set();
    for (const g of groups) {
      if (!g.label || !g.label.trim()) {
        return "Every option group needs a title.";
      }
      if (groupIds.has(g.id)) return "Duplicate option group id.";
      groupIds.add(g.id);
      const members = (form.lines || []).filter((l) => l.option_group_id === g.id && l.product_id);
      if (members.length === 0) {
        return `Group "${g.label}" has no options assigned — add at least one line item to it, or remove the group.`;
      }
      // Each member must be optional (required/recommended can't be in a group).
      const badMember = members.find((m) => (m.line_status || "optional") !== "optional");
      if (badMember) {
        return `Group "${g.label}" contains a ${badMember.line_status} line. Only Optional lines can be members of an option group.`;
      }
      // No duplicate product within the same group.
      const pids = members.map((m) => m.product_id);
      if (new Set(pids).size !== pids.length) {
        return `Group "${g.label}" has the same product listed twice.`;
      }
      // Explicit default must reference one of the members.
      if (g.default_product_id && !pids.includes(g.default_product_id)) {
        return `Group "${g.label}" default option must be one of the assigned products.`;
      }
    }
    // A line referencing a missing group id is invalid.
    const validGroupIds = new Set(groups.map((g) => g.id));
    for (const l of form.lines || []) {
      if (l.option_group_id && !validGroupIds.has(l.option_group_id)) {
        return `A line references option group "${l.option_group_id}" which doesn't exist. Remove the group binding on that line.`;
      }
    }
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      // Strip empty product lines, and scrub stale option_group_id from
      // non-optional lines so the backend doesn't 400.
      const cleanLines = form.lines
        .filter((l) => l.product_id)
        .map((l) => ({
          ...l,
          option_group_id: (l.line_status === "optional") ? (l.option_group_id || null) : null,
        }));
      await api.patch(`/admin/proposals/${id}`, {
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        property_address: form.property_address,
        test_id: form.test_id || null,
        notes: form.notes || null,
        brand: form.brand,
        lines: cleanLines,
        option_groups: form.option_groups || [],
      });
      toast.success("Saved");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const send = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/proposals/${id}/send`);
      // Phase 2L-A109 — show the admin exactly what happened on the
      // customer side. Before A109 the customer email wasn't even
      // attempted, so admins always saw a green tick. Now the toast
      // reflects the real customer_email_status returned by the
      // endpoint.
      if (data.customer_email_status === "sent") {
        toast.success(`Estimate sent to ${data.customer_email}`);
      } else if (data.customer_email_status === "skipped_no_recipient") {
        toast.warning(
          "Estimate marked sent + team notified, but no customer email " +
          "on file — copy the customer link below and share it manually.",
          { duration: 8000 }
        );
      } else if (data.customer_email_status === "failed") {
        toast.error(
          `Estimate marked sent, but the customer email to ` +
          `${data.customer_email} FAILED: ` +
          `${data.customer_email_error || "unknown error"}. ` +
          `Copy the customer link below and share it manually.`,
          { duration: 12000 }
        );
      } else {
        toast.success("Estimate sent + customer link generated");
      }
      try { await navigator.clipboard.writeText(data.public_url); } catch (_) { /* clipboard blocked */ }
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Send failed");
    } finally {
      setSaving(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel this estimate? The customer link will return 410 and they can no longer accept it.")) return;
    try {
      await api.post(`/admin/proposals/${id}/cancel`);
      toast.success("Cancelled");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Cancel failed");
    }
  };

  const copyLink = async () => {
    if (!doc.public_url) {
      toast.error("Send the estimate first to generate a public link");
      return;
    }
    try {
      await navigator.clipboard.writeText(doc.public_url);
      toast.success("Customer link copied to clipboard");
    } catch (_) {
      toast.message(doc.public_url);
    }
  };

  const downloadPdf = () => {
    const a = document.createElement("a");
    a.href = `${BACKEND}/api/admin/proposals/${id}/pdf`;
    a.download = `proposal-${doc.reference}.pdf`;
    // Attach auth via fetch (anchor download won't carry headers)
    fetch(a.href, { headers: { Authorization: `Bearer ${localStorage.getItem("scwt_token")}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        a.href = url;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Failed to download PDF"));
  };

  return (
    <div data-testid="proposal-detail-drawer" className="fixed inset-0 z-50 flex items-stretch justify-end bg-slate-900/50 backdrop-blur-sm">
      <div className="bg-slate-50 w-full max-w-3xl h-full overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700 flex items-center flex-wrap gap-2">
              <span>{doc.reference}</span>
              <span aria-hidden>·</span>
              <StatusBadge status={doc.status} />
              {/* Phase 2J-AB: surface a calm admin-only badge when this
                  proposal originated from the customer-facing Estimate
                  Request click. Detected via the audit_log entry (or
                  the `requested` status). Backend identifiers are
                  unchanged — this is purely a wording aid for
                  Stephen/Admin so the review screen matches what the
                  customer saw on the Pristine Summary. */}
              {(doc.status === "requested"
                || (doc.audit_log || []).some(
                    (a) => a.action === "proposal_request_created"
                  )) && (
                <span
                  data-testid="customer-estimate-request-badge"
                  className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 text-[10px] font-semibold tracking-wider uppercase normal-case"
                  title="This estimate originated from the customer's Estimate Request click on the Pristine Summary PDF."
                  style={{ letterSpacing: "0.02em" }}
                >
                  Customer Estimate Request
                </span>
              )}
            </div>
            <h2 className="font-display text-xl font-semibold text-slate-900 mt-1">
              {form.customer_name || "Untitled estimate"}
            </h2>
          </div>
          <button data-testid="proposal-detail-close" onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Action bar */}
          <div className="flex flex-wrap gap-2">
            {!locked && (
              <Button
                data-testid="proposal-send-btn"
                onClick={send}
                disabled={saving || !!doc.requires_review}
                className="rounded-full font-semibold"
                title={doc.requires_review ? "Linked Aware report still requires Stephen/Admin review" : ""}
              >
                <Send className="w-4 h-4 mr-1.5" /> {doc.status === "draft" ? "Send to customer" : "Resend"}
              </Button>
            )}
            {doc.public_url && (
              <>
                <Button variant="outline" onClick={copyLink} className="rounded-full">
                  <Copy className="w-4 h-4 mr-1.5" /> Copy link
                </Button>
                <a href={doc.public_url} target="_blank" rel="noreferrer">
                  <Button variant="outline" className="rounded-full">
                    <Eye className="w-4 h-4 mr-1.5" /> Preview <ExternalLink className="w-3 h-3 ml-1" />
                  </Button>
                </a>
              </>
            )}
            <Button variant="outline" onClick={downloadPdf} className="rounded-full">
              <FileDown className="w-4 h-4 mr-1.5" /> Download PDF
            </Button>
            {!locked && doc.status !== "draft" && (
              <Button variant="outline" onClick={cancel} className="rounded-full text-rose-600 hover:bg-rose-50 hover:text-rose-700">
                <Trash2 className="w-4 h-4 mr-1.5" /> Cancel estimate
              </Button>
            )}
          </div>

          {locked && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 flex items-start gap-2.5 text-sm text-amber-900">
              <Lock className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                This estimate / proposal is <strong>{doc.status}</strong> and locked. Catalog edits will not affect it.
                {isApproved && doc.approved_at && <span className="block text-xs mt-0.5">Approved {formatDateTimeAU(doc.approved_at)} · Total {money(doc.approved_total_cents)}</span>}
              </div>
            </div>
          )}

          {!locked && doc.requires_review && (
            <div data-testid="proposal-review-required-banner" className="bg-rose-50 border border-rose-200 rounded-xl p-3.5 flex items-start gap-2.5 text-sm text-rose-900">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <strong>Sending blocked — linked Aware report needs Stephen/Admin review.</strong>
                <div className="text-xs mt-1">Resolve the <code className="bg-white/60 px-1 rounded">requires_stephen_review</code> flag on the lab report, then return here to send. Editing the draft is still allowed.</div>
              </div>
            </div>
          )}

          {/* Customer info */}
          <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3">
            <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500">Customer</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase font-bold text-slate-600 tracking-wider">Name</Label>
                <Input data-testid="prop-customer-name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} disabled={locked} className="mt-1" />
              </div>
              <div>
                <Label className="text-[10px] uppercase font-bold text-slate-600 tracking-wider">Email</Label>
                <Input data-testid="prop-customer-email" type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} disabled={locked} className="mt-1" />
              </div>
              <div>
                <Label className="text-[10px] uppercase font-bold text-slate-600 tracking-wider">Phone</Label>
                <Input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} disabled={locked} className="mt-1" />
              </div>
              <div>
                <Label className="text-[10px] uppercase font-bold text-slate-600 tracking-wider">Aware test ID</Label>
                <Input value={form.test_id} onChange={(e) => setForm({ ...form, test_id: e.target.value })} disabled={locked} className="mt-1" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[10px] uppercase font-bold text-slate-600 tracking-wider">Property address</Label>
                <Input value={form.property_address} onChange={(e) => setForm({ ...form, property_address: e.target.value })} disabled={locked} className="mt-1" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[10px] uppercase font-bold text-slate-600 tracking-wider">Internal notes (also shown on customer view)</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} disabled={locked} className="mt-1" rows={2} />
              </div>
            </div>
          </div>

          {/* Phase 2L-A27 Step 3 — Add from preset (admin builder) */}
          {!isApproved && (
            <div className="flex items-center justify-between bg-cyan-50/40 border border-cyan-200 rounded-2xl px-4 py-3">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-cyan-800">
                  Estimate templates
                </div>
                <div className="text-xs text-slate-600 mt-0.5">
                  Insert a saved preset (e.g. <em>Whole Home Filtration — Tank Water Options</em>)
                  to skip rebuilding common groups.
                </div>
              </div>
              <Button
                data-testid="add-from-preset-btn"
                variant="outline"
                size="sm"
                onClick={() => setPresetPickerOpen(true)}
                className="rounded-full whitespace-nowrap"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Add from preset
              </Button>
            </div>
          )}

          {/* Phase 2L-A25 — Choose-one option groups (admin builder) */}
          {!isApproved && (
            <OptionGroupsEditor
              groups={form.option_groups || []}
              lines={form.lines || []}
              products={products}
              onChange={updateGroups}
              locked={locked}
            />
          )}

          {/* Lines */}
          <div className="bg-white rounded-2xl border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500">Line items</div>
              {!locked && (
                <Button variant="outline" data-testid="add-line-btn" onClick={addLine} className="rounded-full" size="sm">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add item
                </Button>
              )}
            </div>
            {isApproved ? (
              <div className="space-y-2">
                {(doc.lines || []).map((l) => <ApprovedLineRow key={l.product_id} line={l} />)}
              </div>
            ) : (
              <div className="space-y-2">
                {form.lines.length === 0 && (
                  <div className="text-center py-6 text-sm text-slate-400">No items yet. Add at least one before sending.</div>
                )}
                {form.lines.map((line, i) => (
                  <LineEditor
                    key={i}
                    line={line}
                    index={i}
                    products={products}
                    optionGroups={form.option_groups || []}
                    locked={locked}
                    onChange={(patch) => updateLine(i, patch)}
                    onRemove={() => removeLine(i)}
                  />
                ))}
              </div>
            )}
            <div className="mt-4 pt-3 border-t border-slate-200 flex items-center justify-between">
              <div data-testid="proposal-subtotal-label" className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                {isApproved
                  ? "Final approved total"
                  : subtotalLabelParts.length === 0
                    ? "Starting total preview"
                    : `Starting total preview (${subtotalLabelParts.join(" + ")})`}
              </div>
              <div data-testid="proposal-subtotal" className="text-xl font-bold tabular-nums text-slate-900">
                {money(isApproved ? doc.approved_total_cents : subtotal)}
              </div>
            </div>
          </div>

          {/* Audit log */}
          {(doc.audit_log || []).length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-4">
              <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500 mb-2">Activity log</div>
              <ul className="space-y-1.5 text-xs text-slate-700">
                {doc.audit_log.slice(-25).reverse().map((a, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="font-mono text-[10px] text-slate-400 mt-0.5 flex-shrink-0">{(a.at || "").slice(0, 19).replace("T", " ")}</span>
                    <span className="font-semibold uppercase text-[10px] tracking-wider text-cyan-700 flex-shrink-0 w-20">{a.action}</span>
                    <span className="text-slate-600 break-words">{a.by || a.to || ""} {a.kind ? `· ${a.kind}` : ""} {a.error ? `· ${a.error}` : ""}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Footer save */}
          {!locked && (
            <div className="sticky bottom-0 bg-white border-t border-slate-200 -mx-6 px-6 py-3 flex justify-end gap-2">
              <Button variant="ghost" onClick={onClose} disabled={saving} className="rounded-full">Close</Button>
              <Button data-testid="proposal-save-btn" onClick={save} disabled={saving} className="rounded-full font-semibold">Save changes</Button>
            </div>
          )}
        </div>
      </div>

      {/* Phase 2L-A27 Step 3 — Preset picker modal */}
      {presetPickerOpen && (
        <PresetPickerModal
          onCancel={() => setPresetPickerOpen(false)}
          onPick={applyPreset}
        />
      )}
      {/* Phase 2L-A32 — Merge-vs-replace confirmation. Only opens when the
          draft already has lines or option_groups (see applyPreset). */}
      {presetMergeChoice && (
        <PresetMergeChoiceModal
          onAdd={() => _applyPresetWithMode(presetMergeChoice.presetId, false)}
          onReplace={() => _applyPresetWithMode(presetMergeChoice.presetId, true)}
          onCancel={() => setPresetMergeChoice(null)}
        />
      )}
    </div>
  );
}

// ============================================================ PRESET PICKER MODAL (Phase 2L-A27 Step 3)

function PresetPickerModal({ onCancel, onPick }) {
  const [presets, setPresets] = useState(null);  // null = loading
  const [search, setSearch] = useState("");
  const [applying, setApplying] = useState(null);  // preset_id being applied

  useEffect(() => {
    let cancelled = false;
    // Server filter to active=true so inactive presets are NOT exposed in the
    // picker — admins manage active/inactive on the dedicated page.
    api.get("/admin/proposal-presets", { params: { active: true } })
      .then(({ data }) => { if (!cancelled) setPresets(data || []); })
      .catch((e) => {
        if (!cancelled) {
          toast.error(formatApiError(e));
          setPresets([]);
        }
      });
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    if (!presets) return [];
    const q = search.trim().toLowerCase();
    if (!q) return presets;
    return presets.filter((p) =>
      (p.name || "").toLowerCase().includes(q) ||
      (p.category || "").toLowerCase().includes(q),
    );
  }, [presets, search]);

  const handlePick = async (presetId) => {
    setApplying(presetId);
    try {
      await onPick(presetId);
    } finally {
      setApplying(null);
    }
  };

  return (
    <div
      data-testid="preset-picker-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white max-w-xl w-full rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-slate-900 text-white px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-cyan-300">Estimate templates</div>
            <h2 className="font-display text-lg font-bold">Add from preset</h2>
          </div>
          <button
            data-testid="preset-picker-close"
            onClick={onCancel}
            className="p-2 rounded-full hover:bg-white/10"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 border-b border-slate-200">
          <Input
            data-testid="preset-picker-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or category…"
            className="h-9 text-sm"
            autoFocus
          />
        </div>

        <div className="max-h-[55vh] overflow-y-auto">
          {presets === null ? (
            <div className="p-6 text-center text-sm text-slate-500">Loading presets…</div>
          ) : filtered.length === 0 ? (
            <div data-testid="preset-picker-empty" className="p-6 text-center text-sm text-slate-500">
              {presets.length === 0
                ? <>No active presets yet. <a href="/admin/proposal-presets" className="text-cyan-700 underline font-semibold">Create one →</a></>
                : "No active presets match that search."}
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {filtered.map((p) => {
                const groupCount = (p.option_groups || []).length;
                const lineCount = (p.lines || []).length;
                const isApplying = applying === p.id;
                return (
                  <li key={p.id}>
                    <button
                      data-testid={`preset-picker-row-${p.id}`}
                      disabled={!!applying}
                      onClick={() => handlePick(p.id)}
                      className="w-full text-left px-5 py-3 hover:bg-slate-50 transition-colors disabled:opacity-50 flex items-start justify-between gap-4"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-semibold text-slate-900 truncate">{p.name}</div>
                          {p.category && (
                            <span className="text-[10px] uppercase font-bold tracking-wider text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded-full">
                              {p.category}
                            </span>
                          )}
                        </div>
                        {p.description && (
                          <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{p.description}</div>
                        )}
                        <div className="text-[11px] text-slate-400 mt-1">
                          {groupCount} group{groupCount === 1 ? "" : "s"}
                          <span className="mx-1">·</span>
                          {lineCount} line{lineCount === 1 ? "" : "s"}
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        {isApplying ? (
                          <span className="text-xs text-slate-500">Adding…</span>
                        ) : (
                          <Plus className="w-4 h-4 text-cyan-700" />
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="bg-slate-50 px-5 py-3 border-t border-slate-200 flex items-center justify-between">
          <a
            href="/admin/proposal-presets"
            className="text-xs text-slate-600 hover:text-slate-900 hover:underline"
          >
            Manage presets →
          </a>
          <Button variant="ghost" onClick={onCancel} size="sm" className="rounded-full">
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================ NEW PROPOSAL MODAL

function NewProposalDialog({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [testId, setTestId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const create = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/proposals", {
        customer_name: name || null,
        customer_email: email || null,
        customer_phone: phone || null,
        property_address: address || null,
        test_id: testId || null,
        brand: "pristine_water_systems",
        lines: [],
      });
      toast.success(`Estimate ${data.reference} created`);
      onCreated(data.id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Create failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
        <h2 className="font-display text-xl font-semibold mb-1">New estimate</h2>
        <p className="text-xs text-slate-500 mb-4">Capture the customer details first — you&apos;ll add line items in the next step.</p>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase font-bold tracking-wider text-slate-600">Customer name</Label>
            <Input data-testid="new-prop-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase font-bold tracking-wider text-slate-600">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold tracking-wider text-slate-600">Phone</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase font-bold tracking-wider text-slate-600">Property address</Label>
            <Input value={address} onChange={(e) => setAddress(e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label className="text-[10px] uppercase font-bold tracking-wider text-slate-600">Aware test ID (optional)</Label>
            <Input value={testId} onChange={(e) => setTestId(e.target.value)} className="mt-1" />
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={submitting} className="rounded-full">Cancel</Button>
          <Button data-testid="new-prop-create" onClick={create} disabled={submitting} className="rounded-full font-semibold">Create &amp; open</Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================ LIST PAGE

export default function AdminProposals() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(searchParams.get("focus"));
  const [showNew, setShowNew] = useState(searchParams.get("new") === "1");
  const [products, setProducts] = useState([]);
  const [showDemo, setShowDemo] = useShowDemoToggle("admin.proposals.showDemo");
  const [archived, setArchived] = useArchiveFilter("admin.proposals.archived");
  // Phase 2L-A125 follow-up — needs-attention chip count. Fetched
  // independently of the row list so the chip badge stays in sync
  // with the underlying data regardless of which filter is active.
  const [attentionCount, setAttentionCount] = useState(0);

  // Strip the ?new=1 flag once consumed so refreshing the page doesn't re-open
  // the modal forever.
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the URL ?focus=... in sync with the open drawer so the link from
  // /admin/lab/:id is deep-linkable.
  useEffect(() => {
    if (selectedId && searchParams.get("focus") !== selectedId) {
      setSearchParams({ focus: selectedId }, { replace: true });
    } else if (!selectedId && searchParams.get("focus")) {
      const next = new URLSearchParams(searchParams);
      next.delete("focus");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line
  }, [selectedId]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter === NEEDS_ATTENTION_FILTER) {
        // Synthetic filter — backend doesn't take status=__needs_attention__,
        // it takes needs_attention=true (orthogonal flag).
        params.needs_attention = "true";
      } else if (statusFilter) {
        params.status = statusFilter;
      }
      if (query.trim()) params.q = query.trim();
      if (showDemo) params.show_demo = "true";
      if (archived !== "active") params.archived = archived;
      const { data } = await api.get("/admin/proposals", { params });
      setRows(data);
    } finally {
      setLoading(false);
    }
  };

  // Phase 2L-A125 follow-up — refresh the chip count badge.
  const loadAttentionCount = async () => {
    try {
      const params = {};
      if (showDemo) params.show_demo = "true";
      if (archived !== "active") params.archived = archived;
      const { data } = await api.get("/admin/proposals/attention-count", { params });
      setAttentionCount(Number(data?.count) || 0);
    } catch {
      // count is best-effort — don't block the page on it
      setAttentionCount(0);
    }
  };

  // Load products once for the line-item picker.
  // Phase 2L-A31: NO `active_only=true` filter — we need ALL products so
  // existing proposal/preset lines that reference inactive products (e.g.
  // the seeded-inactive T20 `filtration_twin_20`, or any product an admin
  // marks inactive AFTER it's been used in presets) can still render their
  // name / image / unit price. The `<select>` dropdown will visually flag
  // inactive products with "(inactive)" so admins don't pick them by
  // accident for new lines.
  useEffect(() => {
    api.get("/admin/products").then(({ data }) => setProducts(data));
  }, []);
  // Reload list when filters change (with a small debounce on query)
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [statusFilter, query, showDemo, archived]);

  // Phase 2L-A125 follow-up — refresh chip count when the demo/archive
  // scoping changes (the count is scoped the same way as the list).
  useEffect(() => {
    loadAttentionCount();
    // eslint-disable-next-line
  }, [showDemo, archived, rows.length]);

  return (
    <PortalShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700">Admin · Estimates</div>
            <h1 data-testid="admin-proposals-heading" className="font-display text-3xl sm:text-4xl font-semibold text-slate-900 mt-2">Customer Estimates</h1>
            <p className="text-sm text-slate-500 mt-2 max-w-2xl">
              Create, send, track and review customer estimates. Status, search and audit history all live here — the customer-facing viewer is at <span className="font-mono text-slate-600">/proposals/{"{token}"}</span>.
            </p>
          </div>
          <Button data-testid="admin-proposals-new" onClick={() => setShowNew(true)} className="rounded-full font-semibold">
            <Plus className="w-4 h-4 mr-1.5" /> New estimate
          </Button>
        </div>

        <div className="flex justify-end mb-3 items-center gap-3">
          <ArchiveStatusFilter value={archived} onChange={setArchived} testId="proposals-archive-filter" />
          <ShowTestDataToggle showDemo={showDemo} onChange={setShowDemo} testId="proposals-show-demo-toggle" />
        </div>

        {/* Filter chips */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {STATUS_TABS.map((t) => (
            <button
              key={t.value || "all"}
              data-testid={`status-filter-${t.value || "all"}`}
              onClick={() => setStatusFilter(t.value)}
              className={`text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full transition-colors ${
                statusFilter === t.value
                  ? "bg-slate-900 text-white"
                  : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50"
              }`}
            >
              {t.label}
            </button>
          ))}
          {/* Phase 2L-A125 follow-up — Needs admin attention chip. */}
          <button
            data-testid="status-filter-needs-attention"
            onClick={() => setStatusFilter(NEEDS_ATTENTION_FILTER)}
            title="Proposals flagged as legacy / incomplete / stale customer requests"
            className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full transition-colors ${
              statusFilter === NEEDS_ATTENTION_FILTER
                ? "bg-amber-600 text-white"
                : attentionCount > 0
                  ? "bg-amber-50 text-amber-800 border border-amber-300 hover:bg-amber-100"
                  : "bg-white text-slate-400 border border-slate-200 hover:bg-slate-50"
            }`}
          >
            Needs attention
            <span
              data-testid="needs-attention-count"
              className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full text-[10px] font-bold ${
                statusFilter === NEEDS_ATTENTION_FILTER
                  ? "bg-white/25 text-white"
                  : attentionCount > 0
                    ? "bg-amber-600 text-white"
                    : "bg-slate-200 text-slate-500"
              }`}
            >
              {attentionCount}
            </span>
          </button>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <input
            data-testid="proposal-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by customer name, email, phone, address or reference…"
            className="w-full pl-10 pr-3 h-10 text-sm border border-slate-300 rounded-lg bg-white focus:outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100 transition"
          />
        </div>

        {/* List */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center">
              <AlertCircle className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No estimates match this filter.</p>
            </div>
          ) : (
            <table data-testid="admin-proposals-table" className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-700 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Reference</th>
                  <th className="text-left px-4 py-3 font-semibold">Customer</th>
                  <th className="text-left px-4 py-3 font-semibold">Property</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="text-right px-4 py-3 font-semibold">Total</th>
                  <th className="text-right px-4 py-3 font-semibold">Updated</th>
                  <th className="text-right px-2 py-3 font-semibold w-20">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((p) => {
                  const isApproved = p.status === "approved";
                  return (
                    <tr key={p.id}
                        data-testid={`proposal-row-${p.id}`}
                        onClick={() => setSelectedId(p.id)}
                        className="hover:bg-slate-50/60 cursor-pointer">
                      <td className="px-4 py-3 font-mono text-xs font-semibold text-slate-900">{p.reference}</td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-900">{p.customer_name || "—"}</div>
                        <div className="text-xs text-slate-500">{p.customer_email || ""}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-600 max-w-xs truncate">{p.property_address || "—"}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1.5">
                          <StatusBadge status={p.status} />
                          {p.needs_admin_attention && (
                            <div
                              data-testid={`proposal-needs-attention-${p.id}`}
                              title="This proposal has incomplete or legacy request data and should be reviewed before sending."
                              className="inline-flex flex-wrap items-center gap-1"
                            >
                              <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300">
                                <AlertCircle className="w-3 h-3" />
                                Needs admin attention
                              </span>
                              {(p.attention_reasons || []).map((reason) => (
                                <span
                                  key={reason}
                                  data-testid={`proposal-attention-reason-${reason}-${p.id}`}
                                  className="inline-block text-[9px] uppercase font-semibold tracking-wider px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200"
                                >
                                  {ATTENTION_REASON_LABEL[reason] || reason}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-semibold">
                        {isApproved ? money(p.approved_total_cents) : <span className="text-slate-400 font-normal">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right text-xs text-slate-500">
                        {(p.updated_at || p.created_at || "").slice(0, 10)}
                      </td>
                      <td className="px-2 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="inline-flex items-center gap-2">
                          {p.archived_at && <ArchiveStatusBadge status="archived" />}
                          <ArchiveActionsMenu
                            recordType="proposal"
                            recordId={p.id}
                            recordDisplay={p.reference}
                            isArchived={!!p.archived_at}
                            onChanged={load}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showNew && (
        <NewProposalDialog
          onClose={() => setShowNew(false)}
          onCreated={(id) => { setShowNew(false); setSelectedId(id); load(); }}
        />
      )}
      {selectedId && (
        <ProposalDetail
          id={selectedId}
          products={products}
          onClose={() => setSelectedId(null)}
          onChanged={load}
        />
      )}
    </PortalShell>
  );
}



// ============================================================ PRESET MERGE-VS-REPLACE MODAL (Phase 2L-A32)

function PresetMergeChoiceModal({ onAdd, onReplace, onCancel }) {
  const [busy, setBusy] = useState(null); // "add" | "replace" | null

  const click = async (mode, fn) => {
    if (busy) return;
    setBusy(mode);
    try {
      await fn();
    } finally {
      // Parent unmounts on success — only matters on failure paths.
      setBusy(null);
    }
  };

  return (
    <div
      data-testid="preset-merge-modal"
      className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}
    >
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-200 p-6">
        <h2 className="font-display text-xl font-bold text-slate-900 mb-2">
          Add preset to this estimate?
        </h2>
        <p className="text-sm text-slate-600 leading-relaxed mb-6">
          This estimate already contains items. You can <strong>add</strong> this preset
          to the existing draft, or <strong>replace</strong> the current draft contents
          with this preset. Customer details, property and notes are not changed either way.
        </p>
        <div className="space-y-2">
          <Button
            data-testid="preset-merge-add-btn"
            disabled={!!busy}
            onClick={() => click("add", onAdd)}
            className="w-full rounded-full bg-cyan-600 hover:bg-cyan-700 text-white"
          >
            {busy === "add" ? "Adding…" : "Add to existing estimate"}
          </Button>
          <Button
            data-testid="preset-merge-replace-btn"
            variant="outline"
            disabled={!!busy}
            onClick={() => click("replace", onReplace)}
            className="w-full rounded-full border-amber-300 text-amber-700 hover:bg-amber-50"
          >
            {busy === "replace" ? "Replacing…" : "Replace current items"}
          </Button>
          <Button
            data-testid="preset-merge-cancel-btn"
            variant="ghost"
            disabled={!!busy}
            onClick={onCancel}
            className="w-full rounded-full text-slate-600"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
